// generate.ts — Deterministic procedural galaxy: a two-armed logarithmic spiral
// with a central bulge, each star getting a small planetary system. The output
// is a plain Galaxy value, ready to cache in valkey and render in the SPA.
import type { AsteroidBelt, Galaxy, Hyperlane, Moon, Planet, PlanetKind, StarSystem } from '../types';
import { makeRng, type Rng } from './rng';
import { pickSpectralClass, SPECTRAL } from './spectral';
import { planetName, starName } from './names';
import { HABITABLE_KINDS } from '../scene/planet/model';

const PLANET_PALETTE: Record<PlanetKind, string[]> = {
  tropical: ['#2fae7a', '#1f9c6b', '#3fc48e'],
  continental: ['#3f8f4a', '#7a8a4f', '#6b5a3a'],
  ocean: ['#2aa6c4', '#1f8aa8', '#3fbcd6'],
  savanna: ['#c79a4a', '#b5853a', '#d6b265'],
  arid: ['#b5602f', '#9c4a28', '#c47a3a'],
  desert: ['#d6b46a', '#c79a4f', '#e0c483'],
  arctic: ['#bcd6e2', '#a8c4d4', '#d4e6ee'],
  tundra: ['#8a7a5f', '#6f6450', '#9c8a6f'],
  alpine: ['#8a8a8e', '#6e7470', '#a0a2a0'],
  volcanic: ['#3a2a26', '#5a3a30', '#2a201e'],
  barren_dry: ['#a89a82', '#8a7d68', '#b5a890'],
  barren_cold: ['#5f6a72', '#4a545c', '#6f7a82'],
  broken: ['#3a3a3e', '#4a3636', '#2a2a2e'],
  frozen: ['#d4e2ea', '#c2d6e2', '#e2eef4'],
  gas: ['#caa46a', '#e8d3a1', '#b88a52'],
  molten: ['#ff6a1a', '#e8480e', '#ff8a2a'],
  toxic: ['#b5c43a', '#9caa2f', '#c9d65a'],
};

// Equilibrium-temperature proxy (°C) at a neutral albedo — mirrors the climate
// model in scene/planet/model.ts (TEMP_K0=472, neutral albedo 0.3 -> factor 0.7).
// The world's CLASS is chosen from this so the rendered biome always matches the
// mean temperature the sidebar will show. Mirrors tempProxyC in the Go generator.
function tempProxyC(lum: number, a: number): number {
  // Empirical mean-surface-temp proxy (°C) at a neutral albedo: warm inner orbits
  // falling to frozen outer ones, with a MODEST star-type shift. Calibrated so the
  // habitable band sits inside the generated orbit range (a≈2..3) for EVERY star —
  // the physical lum/a² would push dim dwarfs' habitable zones below the minimum
  // orbit and freeze their every world. Mirrors tempProxyC in the Go generator and
  // the climate model in scene/planet/model.ts.
  return 45 - 80 * Math.log2(a / 2) + 30 * Math.tanh(0.5 * Math.log2(lum));
}

// Climate (insolation) + geology/atmosphere/humidity rolls -> Stellaris-style class.
// Consumes exactly three rng rolls (geo, atm, wet) in this order. Gas giants are
// chosen by size/orbit before this. Mirrors pickKind in the Go generator.
function pickKind(rng: Rng, lum: number, a: number): PlanetKind {
  const t = tempProxyC(lum, a);
  const geo = rng.next();
  const atm = rng.next();
  const wet = rng.next();
  // Geologically violent (internal heat) — rare, any orbit; or blistering insolation.
  if (geo < 0.04 || t > 320) return 'molten';
  if (geo < 0.16 || t > 180) return 'volcanic';
  // Shattered dead world — very rare.
  if (geo > 0.985) return 'broken';
  // Toxic runaway atmosphere on an otherwise temperate/warm world.
  if (atm < 0.12 && t > -10 && t < 170) return 'toxic';
  // Airless rock: thin/no atmosphere -> barren (dry if warm, cold otherwise).
  if (atm < 0.34) return t > -40 ? 'barren_dry' : 'barren_cold';
  // Atmosphere-bearing habitable span, by temperature + surface water.
  if (t > 75) return 'desert'; // too hot for liquid-water life
  if (t > 48) return wet > 0.55 ? 'tropical' : 'desert';
  if (t > 28) return wet > 0.66 ? 'tropical' : wet > 0.33 ? 'savanna' : 'arid';
  if (t > 10) return wet > 0.6 ? 'ocean' : wet > 0.3 ? 'continental' : 'savanna';
  if (t > -6) return wet > 0.5 ? 'continental' : 'tundra';
  if (t > -30) return wet > 0.45 ? 'tundra' : 'alpine';
  if (t > -75) return 'arctic';
  return 'frozen';
}

// Per-habitable-class carrying-capacity multiplier: lush worlds (ocean/tropical/
// continental) support the most pops, drier/colder ones (savanna/arid/alpine)
// progressively fewer. Mirrors popHabitability in the Go generator.
const POP_HABITABILITY: Partial<Record<PlanetKind, number>> = {
  ocean: 1.0,
  tropical: 1.0,
  continental: 1.0,
  savanna: 0.65,
  arid: 0.4,
  alpine: 0.4,
};
// Carrying capacity (pops, millions) of a prime world at the reference radius;
// scaled by class suitability and by surface area (radius²). Calibrated so a fully
// settled Earth-sized continental/ocean world lands near ~10k pops and the very
// best can approach the 50-city cap. Mirrors popCapacity/popRefRadius in Go.
const POP_CAPACITY = 16_000;
const POP_REF_RADIUS = 0.22;

// pickPops rolls a habitable world's initial resident population (millions).
// Uninhabitable classes (and unsettled habitable ones) come up empty; settled
// worlds fill a fraction of their area- and class-scaled carrying capacity, biased
// low so most worlds are partly settled and a few are fully developed. Consumes 0
// rolls for uninhabitable worlds, else 1–2. Mirrors pickPops in the Go generator.
function pickPops(rng: Rng, kind: PlanetKind, radius: number): number {
  if (!HABITABLE_KINDS.has(kind)) {
    return 0;
  }
  const h = POP_HABITABILITY[kind] ?? 0.5;
  // Marginal classes are settled less often than lush ones.
  if (rng.next() > 0.35 + 0.45 * h) {
    return 0;
  }
  const area = (radius / POP_REF_RADIUS) ** 2;
  const capacity = POP_CAPACITY * h * area;
  const fill = 0.15 + 0.85 * rng.next() ** 1.6;
  return Math.round(capacity * fill);
}

const ARMS = 2;
const ARM_SPREAD = 0.45;
const TURNS = 2.4;

// Kepler's third law: orbital period (game-seconds) = ORBIT_K * a^1.5. At 1× a
// game-second is a wall/calendar second, so ORBIT_K is sized to give realistic
// periods — an a≈2.5 world lands near one year, inner worlds a few months, the
// outermost several years (use the speed control to watch them move). The a^1.5
// relationship is what keeps inner planets faster than outer ones, as in reality.
const ORBIT_K = 8_000_000;
// Bumped whenever the galaxy shape changes; mirrors galaxy.SchemaVersion in the
// Go server, which regenerates galaxies cached under an older schema.
//   10: per-planet deposits (fertility/ore) for the economic simulation.
//   11: per-planet climate summary (server-baked; the offline fallback omits it).
const SCHEMA_VERSION = 11;

// Ore-rich classes — rocky, volcanic, shattered, or dry/cold barrens. Mirrors oreRich
// in the Go generator (the client generator is the offline fallback; exact deposit
// values need not match the server, only the SHAPE must).
const ORE_RICH = new Set<PlanetKind>([
  'volcanic',
  'broken',
  'molten',
  'barren_dry',
  'barren_cold',
  'arid',
  'desert',
  'alpine',
  'tundra',
]);

// makeDeposits rolls a world's extractive endowments (fertility from class
// habitability, ore richer on rocky worlds). Mirrors makeDeposits in the Go generator.
function makeDeposits(rng: Rng, kind: PlanetKind): Record<string, number> {
  let fertility = POP_HABITABILITY[kind] ?? (HABITABLE_KINDS.has(kind) ? 0.5 : 0.12);
  fertility *= rng.range(0.85, 1.15);
  let ore = rng.range(0.3, 1.0);
  if (ORE_RICH.has(kind)) ore += 0.6;
  return { fertility, ore };
}
// How many nearest neighbours each system offers as candidate hyperlane links
// beyond the spanning tree; mirrors hyperlaneK in the Go generator.
const HYPERLANE_K = 6;
// Minimum centre-to-centre spacing between systems (scene units) so star markers
// don't overlap on the galaxy map, enforced by re-rolling a position up to
// MAX_PLACE_TRIES times. Mirrors minSystemSep/maxPlaceTries in the Go generator.
const MIN_SYSTEM_SEP = 2.6;
const MAX_PLACE_TRIES = 40;
// Moons use the same law with a tighter constant so they orbit their planet in
// days-to-weeks rather than the planet's months-to-years.
const MOON_K = 700_000;
const MOON_PALETTE = ['#9a958c', '#b3a89a', '#8a8580', '#c2bcae', '#7e7a73'];
// A moon orbiting inside the Roche limit (~2.44 planet radii for similar density)
// is tidally shredded into a ring instead of surviving as a moon.
const ROCHE_FACTOR = 2.44;
// Period ratios for mean-motion resonances; some adjacent planets snap to one.
const RESONANCES = [2, 1.5, 5 / 3];
const RING_PALETTE = ['#b8a88c', '#cdbfa6', '#a99b82', '#d8cdb6'];

function makeMoons(rng: Rng, planetId: string, planetRadius: number): Moon[] {
  // Bigger planets host more moons; tiny ones rarely any.
  const max = planetRadius > 0.26 ? 3 : planetRadius > 0.18 ? 2 : 1;
  const count = rng.int(0, max);
  const moons: Moon[] = [];
  let a = planetRadius * rng.range(2.4, 3.2);
  for (let i = 0; i < count; i++) {
    moons.push({
      id: `${planetId}-m${i}`,
      radius: planetRadius * rng.range(0.18, 0.34),
      color: rng.pick(MOON_PALETTE),
      semiMajorAxis: a,
      eccentricity: Math.pow(rng.next(), 2) * 0.1,
      period: MOON_K * Math.pow(a, 1.5),
      phase: rng.range(0, Math.PI * 2),
      inclination: rng.range(-0.25, 0.25),
      longitudeOfPeriapsis: rng.range(0, Math.PI * 2),
      tidallyLocked: a < planetRadius * 10,
    });
    a += planetRadius * rng.range(1.4, 2.4);
  }
  return moons;
}

// An asteroid belt drops into a wide gap between two adjacent planets, in ~45%
// of systems that have room for one.
function makeAsteroidBelt(rng: Rng, planets: Planet[]): AsteroidBelt | null {
  if (planets.length < 2 || rng.next() > 0.45) {
    return null;
  }
  const i = rng.int(0, planets.length - 2);
  const inner = planets[i].semiMajorAxis;
  const outer = planets[i + 1].semiMajorAxis;
  const gap = outer - inner;
  if (gap < 1.2) {
    return null;
  }
  return {
    innerRadius: inner + gap * 0.3,
    outerRadius: inner + gap * 0.7,
    count: rng.int(140, 340),
    seed: Math.floor(rng.next() * 0xffffffff),
  };
}

function makePlanets(rng: Rng, star: string, systemId: string, lum: number): Planet[] {
  const count = rng.int(0, 6);
  const planets: Planet[] = [];
  // Semi-major axes grow with a widening gap (mildly Titius–Bode), kept bounded
  // so the whole system frames nicely when the camera flies in.
  let a = rng.range(2.0, 2.6);
  let gap = rng.range(1.1, 1.5);
  for (let i = 0; i < count; i++) {
    // Gas giants: common in the cold outer system, rare close in; they run larger.
    const gasRoll = rng.next();
    const isGas = a >= 5 ? gasRoll < 0.5 : gasRoll < 0.08;
    const radius = isGas ? rng.range(0.3, 0.5) : rng.range(0.12, 0.34);
    // Every other world's class is derived from its climate (insolation) plus
    // geology/atmosphere/humidity rolls, so the look matches the temperature.
    const kind: PlanetKind = isGas ? 'gas' : pickKind(rng, lum, a);
    // Initial resident population (millions) — habitable worlds only; the rest 0.
    const pops = pickPops(rng, kind, radius);
    // Squared roll biases toward near-circular orbits, with the occasional
    // eccentric one.
    const eccentricity = Math.pow(rng.next(), 2) * 0.18;
    // System-scoped so planet ids are globally unique (star names can repeat).
    const id = `${systemId}-p${i}`;
    const roche = radius * ROCHE_FACTOR;
    // Moons inside the Roche limit are disrupted into a ring; the rest survive.
    const allMoons = makeMoons(rng, id, radius);
    const moons = allMoons.filter((m) => m.semiMajorAxis >= roche);
    const disrupted = allMoons.length - moons.length;
    const wantRings = disrupted > 0 || rng.next() < (kind === 'gas' ? 0.55 : 0.1);
    const rings = wantRings
      ? {
          innerRadius: radius * 1.5,
          outerRadius: roche * rng.range(0.82, 1.0),
          color: rng.pick(RING_PALETTE),
          tilt: rng.range(-0.45, 0.45),
        }
      : null;
    planets.push({
      id,
      name: planetName(rng, star, i),
      kind,
      semiMajorAxis: a,
      eccentricity,
      period: ORBIT_K * Math.pow(a, 1.5),
      phase: rng.range(0, Math.PI * 2),
      inclination: rng.range(-0.08, 0.08),
      longitudeOfPeriapsis: rng.range(0, Math.PI * 2),
      radius,
      color: rng.pick(PLANET_PALETTE[kind]),
      pops,
      tidallyLocked: a < 3.0,
      rings,
      moons,
      deposits: makeDeposits(rng, kind),
    });
    // Step to the next orbit: usually a widening gap, but ~35% snap to a
    // mean-motion resonance with this planet (a_next = a * ratio^(2/3)).
    if (rng.next() < 0.35) {
      a *= Math.pow(rng.pick(RESONANCES), 2 / 3);
    } else {
      a += gap;
      gap *= rng.range(1.15, 1.35);
    }
  }
  return planets;
}

// makeHyperlanes wires the systems into a sparse navigable graph: a Euclidean
// minimum spanning tree (every system reachable; each tree edge a chokepoint) plus
// the shortest near-neighbour links until the average degree reaches ~3 (E ≈ 1.5N).
// Pure geometry, no RNG — mirrors hyperlanes() in the Go generator.
function makeHyperlanes(systems: StarSystem[]): Hyperlane[] {
  const n = systems.length;
  if (n < 2) {
    return [];
  }
  const dist = (i: number, j: number): number => {
    const a = systems[i].position;
    const b = systems[j].position;
    const dx = a[0] - b[0];
    const dy = a[1] - b[1];
    const dz = a[2] - b[2];
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  };

  const edges: Array<[number, number]> = [];
  const seen = new Set<string>();
  const key = (a: number, b: number): string => (a < b ? `${a}-${b}` : `${b}-${a}`);
  const addEdge = (a: number, b: number): void => {
    if (a === b) {
      return;
    }
    const k = key(a, b);
    if (seen.has(k)) {
      return;
    }
    seen.add(k);
    edges.push(a < b ? [a, b] : [b, a]);
  };

  // Prim's MST from node 0; best[v]/parent[v] track the cheapest link from the tree
  // to each outside node, ties broken on index.
  const inTree = new Array<boolean>(n).fill(false);
  const best = new Array<number>(n).fill(Infinity);
  const parent = new Array<number>(n).fill(-1);
  best[0] = 0;
  for (let k = 0; k < n; k++) {
    let u = -1;
    let bd = Infinity;
    for (let v = 0; v < n; v++) {
      if (!inTree[v] && best[v] < bd) {
        bd = best[v];
        u = v;
      }
    }
    if (u < 0) {
      break;
    }
    inTree[u] = true;
    if (parent[u] >= 0) {
      addEdge(u, parent[u]);
    }
    for (let v = 0; v < n; v++) {
      if (!inTree[v]) {
        const d = dist(u, v);
        if (d < best[v]) {
          best[v] = d;
          parent[v] = u;
        }
      }
    }
  }

  // Candidate extra links: each system's K nearest neighbours, deduped against the
  // tree, added shortest-first until the average-degree target is met.
  const cands: Array<[number, number]> = [];
  const candSeen = new Set<string>();
  const idx = Array.from({ length: n }, (_, j) => j);
  for (let i = 0; i < n; i++) {
    const order = idx
      .filter((j) => j !== i)
      .sort((a, b) => {
        const da = dist(i, a);
        const db = dist(i, b);
        return da !== db ? da - db : a - b;
      });
    for (let x = 0; x < Math.min(HYPERLANE_K, order.length); x++) {
      const a = Math.min(i, order[x]);
      const b = Math.max(i, order[x]);
      const kk = key(a, b);
      if (seen.has(kk) || candSeen.has(kk)) {
        continue;
      }
      candSeen.add(kk);
      cands.push([a, b]);
    }
  }
  cands.sort((p, q) => {
    const dp = dist(p[0], p[1]);
    const dq = dist(q[0], q[1]);
    return dp !== dq ? dp - dq : p[0] !== q[0] ? p[0] - q[0] : p[1] - q[1];
  });
  const target = Math.floor((3 * n) / 2); // average degree 3 → E = 1.5N
  for (const [a, b] of cands) {
    if (edges.length >= target) {
      break;
    }
    addEdge(a, b);
  }

  return edges.map(([a, b]) => ({ a: systems[a].id, b: systems[b].id }));
}

// True when (x,y,z) clears MIN_SYSTEM_SEP from every already-placed position.
function farFromAll(placed: Array<[number, number, number]>, x: number, y: number, z: number): boolean {
  const min2 = MIN_SYSTEM_SEP * MIN_SYSTEM_SEP;
  for (const p of placed) {
    const dx = p[0] - x;
    const dy = p[1] - y;
    const dz = p[2] - z;
    if (dx * dx + dy * dy + dz * dz < min2) {
      return false;
    }
  }
  return true;
}

export function generateGalaxy(seed: number, count = 220): Galaxy {
  const rng = makeRng(seed);
  const systems: StarSystem[] = [];
  const placed: Array<[number, number, number]> = [];
  for (let i = 0; i < count; i++) {
    const arm = i % ARMS;
    // Re-roll the spiral position until it clears MIN_SYSTEM_SEP from the systems
    // placed so far (or we run out of tries in a crowded patch).
    let x = 0;
    let y = 0;
    let z = 0;
    for (let attempt = 0; attempt < MAX_PLACE_TRIES; attempt++) {
      const t = Math.pow(rng.next(), 0.65);
      const angle = t * TURNS * Math.PI * 2 + (arm / ARMS) * Math.PI * 2;
      const dist = 4 + t * 52;
      const scatter = ARM_SPREAD * dist * 0.12;
      x = Math.cos(angle) * dist + rng.range(-scatter, scatter);
      z = Math.sin(angle) * dist + rng.range(-scatter, scatter);
      y = rng.range(-2.2, 2.2) * (1 - t * 0.6);
      if (attempt === MAX_PLACE_TRIES - 1 || farFromAll(placed, x, y, z)) {
        break;
      }
    }
    placed.push([x, y, z]);
    const spectralClass = pickSpectralClass(rng.next());
    const [rmin, rmax] = SPECTRAL[spectralClass].radius;
    const name = starName(rng);
    const systemId = `sys-${i}`;
    const planets = makePlanets(rng, name, systemId, SPECTRAL[spectralClass].lum);
    systems.push({
      id: systemId,
      name,
      spectralClass,
      position: [x, y, z],
      radius: rng.range(rmin, rmax),
      planets,
      asteroidBelt: makeAsteroidBelt(rng, planets),
    });
  }
  return { seed, version: SCHEMA_VERSION, systems, hyperlanes: makeHyperlanes(systems) };
}
